import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const REVIEWS = [
  {
    quote: "Petraloc delivered certified translations of our clinical trial protocols across 14 languages — on time, every time. The medical accuracy is what kept us coming back.",
    name: "Dr. Helena Vargas",
    role: "Director of Clinical Operations",
    industry: "Pharmaceutical",
  },
  {
    quote: "Court-certified, NDA-bound, and consistently fast. They handle our cross-border discovery work with the kind of rigor we expect from outside counsel itself.",
    name: "Marcus Whitfield",
    role: "Senior Litigation Counsel",
    industry: "Legal",
  },
  {
    quote: "We needed Haitian Creole and Wolof speakers for refugee resettlement documentation. Petraloc was the only firm that delivered native specialists, not back-translations.",
    name: "Amaya Okafor",
    role: "Program Lead, Humanitarian Response",
    industry: "Government & NGO",
  },
  {
    quote: "Their MTPE workflow cut our localization spend by roughly a third without compromising the brand voice — and the QA pass caught issues our AI vendor had missed.",
    name: "Lukas Brenner",
    role: "Head of Global Content",
    industry: "Technology",
  },
];

const AUTO_MS = 6500;

export default function TestimonialCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => setIdx((i) => (i + 1) % REVIEWS.length), AUTO_MS);
    return () => clearTimeout(timer.current);
  }, [idx, paused]);

  const go = (delta) => setIdx((i) => (i + delta + REVIEWS.length) % REVIEWS.length);

  const r = REVIEWS[idx];

  return (
    <div
      data-testid="testimonial-carousel"
      className="relative max-w-4xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="bg-white border border-[#E5E7EB] rounded-md p-8 lg:p-12 relative overflow-hidden">
        <Quote className="w-10 h-10 text-[#00D1B2]/30 mb-5" />
        <div className="min-h-[160px] lg:min-h-[140px]">
          {REVIEWS.map((rv, i) => (
            <div
              key={i}
              className={`transition-all duration-700 ease-out ${
                i === idx ? "opacity-100 translate-y-0 relative" : "opacity-0 absolute inset-x-8 lg:inset-x-12 translate-y-2 pointer-events-none"
              }`}
              data-testid={i === idx ? "testimonial-active" : undefined}
            >
              <p className="text-[17px] lg:text-[19px] text-[#222222] leading-relaxed font-light">
                "{rv.quote}"
              </p>
              <div className="mt-7 pt-5 border-t border-[#E5E7EB] flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[14px] font-semibold text-[#071D49]">{rv.name}</p>
                  <p className="text-[12px] text-[#5B6472] mt-0.5">
                    {rv.role} · {rv.industry}
                  </p>
                </div>
                <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#00D1B2]">
                  Verified client · {String(i + 1).padStart(2, "0")} / {String(REVIEWS.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* controls */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="testimonial-dots">
          {REVIEWS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Review ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === idx ? "w-8 bg-[#071D49]" : "w-4 bg-[#D1D5DB] hover:bg-[#5B6472]"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => go(-1)}
            data-testid="testimonial-prev"
            aria-label="Previous review"
            className="w-10 h-10 rounded-md border border-[#E5E7EB] flex items-center justify-center text-[#071D49] hover:border-[#00D1B2] hover:text-[#00A990] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => go(1)}
            data-testid="testimonial-next"
            aria-label="Next review"
            className="w-10 h-10 rounded-md border border-[#E5E7EB] flex items-center justify-center text-[#071D49] hover:border-[#00D1B2] hover:text-[#00A990] transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
